import { FC } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { useIsDesktop } from "@hooks/layout";

import { ERoutes } from "@utils/constants";

import { AccountType, useAccountType } from "@/contexts/AccountTypeContext";

import * as Styled from "../styles";
import { THandleNextStepProp } from "../utils";

const FreePill = () => (
  <span
    style={{
      fontSize: "0.6875rem",
      fontWeight: 600,
      textTransform: "uppercase",
      letterSpacing: "0.5px",
      padding: "3px 10px 5px",
      borderRadius: "10px",
      background: "linear-gradient(100deg, rgb(47, 151, 53) 0%, rgb(46, 125, 50) 45%, rgb(54, 128, 58) 100%)",
      margin: "0 3px",
      color: "#f0e8e2",
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif',
      verticalAlign: "middle",
      position: "relative",
      top: "-1px",
      paddingTop: "4px",
      display: "inline-flex",
      alignItems: "center",
      gap: "6px",
    }}
  >
    <svg width="13" height="10" viewBox="0 0 24 18" fill="currentColor" style={{ flexShrink: 0 }}>
      <path d="M23.498 2.686a2.994 2.994 0 0 0-2.112-2.12C19.544 0 12 0 12 0S4.456 0 2.614.566A2.994 2.994 0 0 0 .502 2.686C0 4.57 0 8.5 0 8.5s0 3.93.502 5.814a2.994 2.994 0 0 0 2.112 2.12C4.456 17 12 17 12 17s7.544 0 9.386-.566a2.994 2.994 0 0 0 2.112-2.12C24 12.43 24 8.5 24 8.5s0-3.93-.502-5.814zM9.545 12.068V4.932L15.818 8.5l-6.273 3.568z" />
    </svg>
    Free
  </span>
);

type FinalProps = {
  onClose: () => void;
  route: ERoutes;
  variant: "positive" | "negative" | "error";
  icon: React.ReactNode;
  buttonIcon?: React.ReactNode;
  onRestart?: () => void;
};

export const Final: FC<Pick<THandleNextStepProp, "type"> & FinalProps> = ({ type, onClose, route, buttonIcon, onRestart }) => {
  const isDesktop = useIsDesktop();
  const { accountType } = useAccountType();
  const isSub = accountType === AccountType.SUBSCRIPTION;
  const ns = isDesktop ? "questionFlow" : "questionFlowMobile";
  const keyPrefix = isSub ? `subscriptionVariant.finalSteps.${type}` : `finalSteps.${type}`;
  const { t } = useTranslation(ns, { keyPrefix });
  const navigate = useNavigate();

  const handleClose = () => {
    onClose();
    setTimeout(() => navigate(route), 300);
  };

  return (
    <Styled.ContentWrapper>
      <Styled.ResultTopGroup>
        <Styled.Heading>{t("title")}</Styled.Heading>
        <Styled.Description>
          {type === "giveCredit" && !isSub ? (
            <Trans t={t} i18nKey="description" components={[<FreePill />]} />
          ) : (
            t("description")
          )}
        </Styled.Description>
      </Styled.ResultTopGroup>
      <Styled.PrimaryButton $fullWidth onClick={handleClose}>
        {buttonIcon}
        {t("buttonText")}
      </Styled.PrimaryButton>
      {onRestart && (
        <Styled.RestartLink type="button" onClick={onRestart}>
          Restart Test
        </Styled.RestartLink>
      )}
    </Styled.ContentWrapper>
  );
};
